import React, { memo, useEffect, useRef, useState } from 'react'
import {
    Container,
    Button,
    ListGroup,
    ButtonGroup
} from 'react-bootstrap'
import {
    EditorContent,
    useEditor,
} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import { Link as RouterLink } from 'react-router-dom';
import {
    Bold,
    Italic,
    Underline as UnderlineIcon,
    List,
    Link2,
    Paperclip,
    Image,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Code,
} from 'react-feather'
import parse from 'html-react-parser';
import { extractUploadFileName, MIME_TYPES } from 'config/constants'
import AppFileUploadForPdf from 'components/shared/FormComponents/AppFileUploadForPdf'
import { useField, useFormikContext } from 'formik'
import { cloneDeep } from 'lodash'
import TextAlign from '@tiptap/extension-text-align'
import { isValidValue } from "helpers/common"
import CharacterCount from '@tiptap/extension-character-count'
import confirmDialog from "utils/confirmationDialog"
import SpeechToTextButton from "components/shared/FormComponents/SpeechToTextButton"

export default function CustomEditor({ style, name, attachmentName, previewMode = false, limit = 1500, className = '', speechToText = false, speechToTextOptions = {} }) {
    console.log("attachmentName", attachmentName)
    const { setFieldValue, setFieldTouched, values, submitCount } = useFormikContext();
    const [field, meta] = useField(name);
    const [attachmentField, attachmentMeta] = useField(attachmentName);
    const [charCount, setCharCount] = useState(0);
    const interimRangeRef = useRef(null);
    const isDictatingRef = useRef(false);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                code: false,
                codeBlock: false,
            }),
            Underline,
            Link.configure({
                openOnClick: false,
                autolink: true,
                linkOnPaste: true,
                HTMLAttributes: {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            CharacterCount.configure({
                limit,
            }),
        ],
        content: isValidValue(field.value) ? field.value : "",
        onUpdate: ({ editor }) => {
            setFieldValue(name, editor.getHTML());
            setCharCount(editor.storage.characterCount.characters());
        },
        onBlur: () => {
            setFieldTouched(name, true);
        }
    });

    useEffect(() => {
        // setContent rebuilds the whole document, which would invalidate the interim
        // speech range mid-dictation; onUpdate keeps formik in sync meanwhile anyway.
        if (isDictatingRef.current) return;
        if (editor && field.value !== editor.getHTML()) {
            editor.commands.setContent(isValidValue(field.value) ? field.value : '');
        }
    }, [field.value, editor]);

    const handleFileUpload = (name, value) => {
        let attachmentData = cloneDeep(attachmentField?.value);

        // Ensure it's an array
        if (!Array.isArray(attachmentData)) {
            attachmentData = [];
        }

        attachmentData.push({
            attachment_key: value,
            attachment_name: value,
        });

        setFieldValue(attachmentName, attachmentData);
    };

    const handleRemoveAttachment = async (index) => {
        const isConfirm = await confirmDialog();
        if (!isConfirm) return;

        // Remove the attachment link from the editor content
        const attachmentData = cloneDeep(attachmentField)?.value
        const data = attachmentData?.filter((item, i) => i != index)
        setFieldValue(attachmentName, data)
    }

    const addImage = () => {
        const url = window.prompt('Enter the URL of the image:')
        if (url) {
            editor.chain().focus().setImage({ src: url }).run()
        }
    }

    // Only take focus if the editor already had it, so dictation does not drag the
    // caret back here several times a second while the user works elsewhere.
    const speechChain = () => (editor.isFocused ? editor.chain().focus() : editor.chain());

    const insertAtCursor = (text) => {
        const { from, to: selectionTo } = editor.state.selection;
        const previousCharacter = from > 1 ? editor.state.doc.textBetween(from - 1, from, ' ') : '';
        const separator = previousCharacter && !/\s/.test(previousCharacter) ? ' ' : '';
        const sizeBefore = editor.state.doc.content.size;

        speechChain().insertContent(`${separator}${text}`).run();

        // insertContent parses its argument as HTML, CharacterCount can truncate it, and
        // a non-empty selection is replaced, so the document grows by inserted - deleted.
        const deleted = selectionTo - from;
        const end = from + (editor.state.doc.content.size - sizeBefore) + deleted;
        if (end <= from) return null;
        return { from, to: end, text: editor.state.doc.textBetween(from, end, ' ') };
    };

    // Interim speech is inserted as ordinary text and rewritten in place as the model
    // refines it; only finalised segments are left in the document for good.
    const clearInterimTranscript = () => {
        const range = interimRangeRef.current;
        interimRangeRef.current = null;
        if (!range || !editor || editor.isDestroyed) return;

        // The document can still be rebuilt underneath us (a formik resync, an edit in
        // another tab of the form), which invalidates the stored positions. Delete only
        // when the interim text is provably still sitting where we put it.
        if (range.to > editor.state.doc.content.size) return;
        if (editor.state.doc.textBetween(range.from, range.to, ' ') !== range.text) return;

        speechChain().deleteRange({ from: range.from, to: range.to }).run();
    };

    const showInterimTranscript = (interim) => {
        clearInterimTranscript();
        interimRangeRef.current = interim ? insertAtCursor(interim) : null;
    };

    const insertTranscript = (transcript) => {
        clearInterimTranscript();
        insertAtCursor(transcript);
        speechToTextOptions.onTranscript?.(transcript);
    };

    if (!editor) {
        return null
    }

    return (
        <>
            {
                previewMode ? <div className="my-3">{parse(field?.value)}</div> :
                    <>
                        <div style={style} className="border editor rounded-top p-2 bg-light d-flex flex-wrap align-items-center">
                            <ButtonGroup className="me-2 mb-1">
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().toggleBold().run()}
                                    active={editor.isActive('bold')}
                                    size="sm"
                                >
                                    <Bold size={16} />
                                </Button>
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().toggleItalic().run()}
                                    active={editor.isActive('italic')}
                                    size="sm"
                                >
                                    <Italic size={16} />
                                </Button>
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                                    active={editor.isActive('underline')}
                                    size="sm"
                                >
                                    <UnderlineIcon size={16} />
                                </Button>
                            </ButtonGroup>

                            <ButtonGroup className="me-2 mb-1">
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                                    active={editor.isActive('bulletList')}
                                    size="sm"
                                >
                                    <List size={16} />
                                </Button>
                            </ButtonGroup>

                            <ButtonGroup className="me-2 mb-1">
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                                    active={editor.isActive({ textAlign: 'left' })}
                                    size="sm"
                                >
                                    <AlignLeft size={16} />
                                </Button>
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                                    active={editor.isActive({ textAlign: 'center' })}
                                    size="sm"
                                >
                                    <AlignCenter size={16} />
                                </Button>
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                                    active={editor.isActive({ textAlign: 'right' })}
                                    size="sm"
                                >
                                    <AlignRight size={16} />
                                </Button>
                                <Button
                                    variant="light"
                                    onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                                    active={editor.isActive({ textAlign: 'justify' })}
                                    size="sm"
                                >
                                    <AlignJustify size={16} />
                                </Button>
                            </ButtonGroup>

                            <ButtonGroup className="me-2 mb-1">
                                <Button
                                    variant="light"
                                    onClick={() => {
                                        const previousUrl = editor.getAttributes('link').href
                                        let url = window.prompt('Enter URL')

                                        if (url === null) return

                                        if (url === '') {
                                            editor.chain().focus().extendMarkRange('link').unsetLink().run()
                                            return
                                        }

                                        if (!/^https?:\/\//i.test(url)) {
                                            url = 'https://' + url
                                        }

                                        // ✅ If no text is selected, insert the link as text
                                        if (editor.state.selection.empty) {
                                            editor
                                                .chain()
                                                .focus()
                                                .insertContent(`<a href="${url}" target="_blank">${url}</a>`)
                                                .run()
                                        } else {
                                            editor
                                                .chain()
                                                .focus()
                                                .extendMarkRange('link')
                                                .setLink({ href: url })
                                                .run()
                                        }
                                    }}
                                    active={editor.isActive('link')}
                                    size="sm"
                                >
                                    <Link2 size={16} />
                                </Button>


                                {
                                    attachmentName &&
                                    <div className="">
                                        <AppFileUploadForPdf
                                            // disabled={previewMode}
                                            disableListing={true}
                                            name={attachmentName}
                                            acl={"public-read"}
                                            accept="image/*, application/pdf, .doc, .docx"
                                            allowedTypes={[MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.jpg, MIME_TYPES.webp, MIME_TYPES.doc, MIME_TYPES.docx]}
                                            className={`form-control`}
                                            onChange={handleFileUpload}
                                            role="button"
                                        />
                                    </div>
                                }
                            </ButtonGroup>


                        </div>

                        <div className={`border border-top-0 tip-tap-custom rounded-bottom p-3 ${className ? className : ""}`}>
                            <EditorContent editor={editor} />
                        </div>
                        <div className='d-flex justify-content-between'>
                            <div className='mt-2'>
                                {speechToText && (
                                    <SpeechToTextButton
                                        {...speechToTextOptions}
                                        onStart={() => {
                                            isDictatingRef.current = true;
                                            speechToTextOptions.onStart?.();
                                        }}
                                        onStop={() => {
                                            isDictatingRef.current = false;
                                            interimRangeRef.current = null;
                                            speechToTextOptions.onStop?.();
                                        }}
                                        onInterimTranscript={showInterimTranscript}
                                        onTranscript={insertTranscript}
                                    />
                                )}
                            </div>
                            <div style={{ textAlign: "right" }}>
                                <small className={`text-${charCount >= limit ? "danger" : "muted"}`}>
                                    {charCount}/{limit}
                                </small>
                            </div>
                        </div>
                        {/* {meta.touched && meta.error ? (
                            <div className="error">{meta.error}</div>
                        ) : null} */}

                        {(meta.touched || submitCount > 0) && meta.error && (
                            <div className="text-danger small mt-1">
                                {meta.error}
                            </div>
                        )}
                    </>
            }

            {console.log("attachmentField?.value", attachmentField?.value)}
            {isValidValue(attachmentField?.value) && attachmentField?.value?.length > 0 && (
                <div className="mt-3">
                    <h6 className="text-muted">Attachments</h6>
                    <ListGroup>
                        {attachmentField?.value?.map((file, index) => (
                            <ListGroup.Item key={index} className="d-flex align-items-center">
                                <Paperclip size={16} className="me-2 text-secondary" />
                                <RouterLink target='_blank' to={file?.attachment_name} className="text-truncate" style={{ maxWidth: '300px' }}>
                                    {extractUploadFileName(file?.attachment_name)}
                                </RouterLink>
                                {
                                    !previewMode && <Button
                                        variant=""
                                        size="sm"
                                        className="ms-auto text-danger"
                                        onClick={() => handleRemoveAttachment(index)}
                                    >
                                        Remove
                                    </Button>
                                }
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </div>
            )}
        </>
    )
}
